import { createAsyncThunk, createSlice, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit'
import type { AnyAction } from '@reduxjs/toolkit'
import { login, logout, changePassword } from 'api/authentications'
import type { LoginParams, ChangePasswordParams, LoginResponse } from 'api/authentications/types'
import type { RootState } from 'app/store'
import type { CommonState } from './types'
import { LoadingTypes } from './types'
import { isPendingAction, isRejectedAction } from './utils'

export const fetchLogin = createAsyncThunk('authentications/login', async (params: LoginParams) => {
  return await login(params)
})
export const fetchLogout = createAsyncThunk('authentications/logout', async () => {
  return await logout()
})
export const fetchChangePassword = createAsyncThunk('authentications/change', async (params: ChangePasswordParams) => {
  return await changePassword(params)
})

const pendingAction = (action: AnyAction) => {
  return isPendingAction(action, isPending(fetchLogin, fetchLogout, fetchChangePassword))
}
const rejectAction = (action: AnyAction) => {
  return isRejectedAction(action, isRejected(fetchLogout, fetchChangePassword))
}

type AuthenTicationsState = CommonState & {
  token: string
}

const initialState: AuthenTicationsState = {
  loading: LoadingTypes.Idle,
  error: '',
  token: '',
}

export const authenticationsSlice = createSlice({
  name: 'authentications',
  initialState,
  reducers: {
    initialize: state => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchLogin.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
      // パスワード変更の成功で succeeded を使うので loading は初期値で良い
      Object.assign(state, initialState, { token: action.payload.token })
    })
    builder.addCase(fetchLogin.rejected, state => {
      state.loading = LoadingTypes.Failed
    })
    builder.addCase(fetchLogout.fulfilled, state => {
      Object.assign(state, initialState)
    })
    builder.addCase(fetchChangePassword.fulfilled, state => {
      Object.assign(state, { loading: LoadingTypes.Succeeded })
    })
    builder.addMatcher(pendingAction, state => {
      state.loading = LoadingTypes.Pending
      state.error = ''
    })
    builder.addMatcher(rejectAction, (state, action) => {
      state.loading = LoadingTypes.Failed
      state.error = action.error?.message ?? 'error'
    })
  },
})

export const { initialize } = authenticationsSlice.actions

export const selectAuthentications = (state: RootState) => state.authentications

export default authenticationsSlice.reducer
